import { isOnline } from '@canvas/network';
import { waitForRetry, type WaitForRetryOptions } from '@canvas/retry';

import { type State } from '../state-machine/network-state-machine';

import {
  type ConnectionErrorStatesMetadata,
  PersistentConnectionError,
  type PersistentConnectionErrorCounts,
} from './errors';
import { WaitForPageVisibilityType, waitForPageVisibility, userCanSeePage } from './wait';

/**
 * How long to wait between reconnect attempts
 */
const DEFAULT_BACKOFF_TIME = 1000;
/**
 * If it has been this long since the last connection attempt, then reset the retry mechanism
 */
export const RESET_DURATION = 120_000;
/**
 * If failing more times than this, report an error
 */
export const RETRY_ALERT_THRESHOLD = 6;

const socketWaitForRetry = ({
  retryTimeMs = DEFAULT_BACKOFF_TIME,
  ...other
}: WaitForRetryOptions) =>
  waitForRetry({
    retryTimeMs,
    ...other,
  });

export type Waiter = {
  wait: (connectionAttempts: number) => Promise<void>;
};
const defaultWaiter: Waiter = {
  wait: async (attempt) => {
    await socketWaitForRetry({ attempt });
  },
};

type ReconnectErrorInfo = {
  error: Error | null;
  state: State | null;
};

export class SocketReconnectDebounce {
  private connectionAttempt = 0;
  private connectionAttemptWhileOffline = 0;

  private connectionErrors: Map<string, ReconnectErrorInfo[]> = new Map();
  private lastConnectionError: Error | null = null;
  private lastConnectionTime: number | null = null;
  private lastConnectionState: State | null = null;

  constructor(
    private readonly onFailingRetry?: ((error: Error) => void) | undefined,
    private readonly waiter: Waiter = defaultWaiter,
  ) {}

  /**
   * Returns an error that sums up the errors received since starting retry
   * attempts, with the 'root' (majority) error listed in the error message and
   * the overall error counts in the error metadata.
   */
  private getAggregateError() {
    const errorCounts: PersistentConnectionErrorCounts = {};

    let maxErrorCount = 0;
    let maxErrorInfo: ReconnectErrorInfo | undefined;

    let noMajority = false;

    for (const [errorName, errorSet] of this.connectionErrors) {
      const errorStates: ConnectionErrorStatesMetadata = {};

      let maxStateCount = 0;
      let maxStateErrorInfo: ReconnectErrorInfo | undefined;

      for (const errorInfo of errorSet) {
        const stateCount = (errorStates[errorInfo.state ?? 'unknown'] ?? 0) + 1;
        errorStates[errorInfo.state ?? 'unknown'] = stateCount;

        if (stateCount > maxStateCount) {
          maxStateCount = stateCount;
          maxStateErrorInfo = errorInfo;
        }
      }

      errorCounts[errorName] = errorStates;

      if (errorSet.length > maxErrorCount) {
        maxErrorCount = errorSet.length;
        maxErrorInfo = maxStateErrorInfo;
        noMajority = false;
      } else if (errorSet.length === maxErrorCount) {
        noMajority = true;
      }
    }

    if (noMajority) {
      return new PersistentConnectionError(
        this.lastConnectionError ?? new Error('Unknown error reason.'),
        errorCounts,
        this.lastConnectionState ?? undefined,
      );
    }

    return new PersistentConnectionError(
      maxErrorInfo?.error ?? new Error('Unknown error reason.'),
      errorCounts,
      maxErrorInfo?.state ?? undefined,
    );
  }

  /**
   * Caller awaits this before attempting to connect. It will add retry backoff and jitter based
   * on the retry attempt.
   */
  public async debounceReconnection() {
    if (
      this.lastConnectionTime !== null &&
      this.timeSinceLastConnection() > RESET_DURATION
    ) {
      // reset the retry mechanism if time since last attempt was long (successfully connect for some time)
      this.resetAttempts();
    }

    if (
      this.connectionAttempt - this.connectionAttemptWhileOffline ===
      RETRY_ALERT_THRESHOLD
    ) {
      // Failing to connect so many times if the user is online in a retryable manner is a problem that should be reported
      this.onFailingRetry?.(this.getAggregateError());
      this.lastConnectionError = null;
      this.connectionErrors = new Map();
    }

    await this.waiter.wait(this.connectionAttempt);

    this.lastConnectionTime = Date.now();
    this.connectionAttempt++;

    if (this.connectionAttempt > 1) {
      const isClientOnline = await isOnline();
      if (!isClientOnline) {
        this.connectionAttemptWhileOffline++;
      }
    }
  }

  /**
   * In some cases you might receive information that will deliberate change the
   * state of the system in such a way that we want to start counting our
   * attempts from scratch. In such cases you want to call this method.
   *
   * Some potential examples:
   * - Changing permissions from write to read only (closes socket) and changed
   *   back to write (opens it again).
   *   In this case, the socket failing and the attempting reconnect again is intentional.
   */
  public resetAttempts() {
    this.lastConnectionTime = null;
    this.connectionAttempt = 0;
    this.connectionAttemptWhileOffline = 0;
    this.connectionErrors = new Map();
    this.lastConnectionError = null;
    this.lastConnectionState = null;
  }

  private timeSinceLastConnection() {
    if (this.lastConnectionTime === null) {
      return 0;
    }

    return Date.now() - this.lastConnectionTime;
  }

  /**
   * For reporting purposes. If reconnects consistently fail this.onFailingRetry will be called next
   * attempt with the error stored here.
   */
  public captureError(error: Error | null) {
    this.lastConnectionError = error;

    const errorMessage = error?.message ?? 'Unknown error reason.';
    const errorSet = this.connectionErrors.get(errorMessage) ?? [];

    const errorInfo = { error, state: this.lastConnectionState };
    errorSet.push(errorInfo);

    this.connectionErrors.set(errorMessage, errorSet);

    if (error) {
      console.debug('[collab] connection attempt failure', error);
    }
  }

  public captureErrorState(state: State) {
    this.lastConnectionState = state;
  }

  get attempt() {
    return this.connectionAttempt;
  }

  get onlineAttempts() {
    return this.connectionAttempt - this.connectionAttemptWhileOffline;
  }
}

export const defaultWaitForUserCanSeePage = () => waitForPageVisibility(userCanSeePage);

export class BrowserWaiter implements Waiter {
  constructor(
    private readonly waitForUserCanSeePage = defaultWaitForUserCanSeePage,
    private readonly waitForRetry = socketWaitForRetry,
  ) {}
  /**
   * Counter for increasing the extra time added by the retry backoff factor
   */
  #backoffCounter = 0;
  public get backoffCounter() {
    return this.#backoffCounter;
  }

  async wait(attempts: number) {
    this.#backoffCounter = Math.min(attempts, this.#backoffCounter);
    const retryWaitPromise = this.waitForRetry({
      attempt: this.#backoffCounter,
    });

    const visibilityState = this.waitForUserCanSeePage();
    if (visibilityState.type === WaitForPageVisibilityType.WAITING) {
      this.#backoffCounter = 0;

      await visibilityState.wait.promise;
      return;
    }

    await retryWaitPromise;
    this.#backoffCounter++;
  }
}
